import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { 
  QrCode, ArrowLeft, Phone, Wrench, MapPin, User, AlertCircle, Calendar, 
  FileText, Shield, CheckCircle, XCircle, Clock, ChevronDown, ChevronUp,
  BookOpen, AlertTriangle
} from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusConfig = {
  working: { 
    label: "Working", 
    color: "bg-[#16A34A]", 
    bgColor: "bg-[#16A34A]/10",
    textColor: "text-[#16A34A]",
    icon: CheckCircle 
  },
  under_maintenance: { 
    label: "Under Maintenance", 
    color: "bg-[#F59E0B]", 
    bgColor: "bg-[#F59E0B]/10",
    textColor: "text-[#F59E0B]",
    icon: Clock 
  },
  not_working: { 
    label: "Not Working", 
    color: "bg-[#DC2626]", 
    bgColor: "bg-[#DC2626]/10",
    textColor: "text-[#DC2626]",
    icon: XCircle 
  },
};

const QRPublicPage = () => {
  const navigate = useNavigate();
  const { code } = useParams();
  const [qrData, setQRData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAllLogs, setShowAllLogs] = useState(false);
  const [expandedSections, setExpandedSections] = useState({
    instructions: true,
    precautions: true,
    logs: true
  });

  useEffect(() => {
    fetchQRData();
  }, [code]);

  const fetchQRData = async () => {
    try {
      const response = await fetch(`${API}/qr/${code}/public`);
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || "QR code not found");
      }

      setQRData(data);
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const handleAssign = () => {
    navigate(`/assign/${code}`);
  };

  const handleCall = (phone) => {
    if (phone) {
      window.location.href = `tel:${phone}`;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-[#2563EB] border-t-transparent" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] p-6">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate("/")}
          className="mb-4"
        >
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <Card className="bg-white shadow-sm">
          <CardContent className="p-8 text-center">
            <div className="w-16 h-16 bg-[#DC2626]/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertCircle className="w-8 h-8 text-[#DC2626]" />
            </div>
            <h2 className="text-xl font-semibold text-[#1F2933] mb-2" style={{ fontFamily: 'Manrope' }}>
              {error}
            </h2>
            <p className="text-[#6B7280] mb-4">
              This QR code may not exist or has been blocked.
            </p>
            <Button onClick={() => navigate("/")} className="bg-[#2563EB] hover:bg-[#1D4ED8]">
              Go Home
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Unassigned QR
  if (qrData?.status === "unassigned") {
    return (
      <div className="min-h-screen bg-[#F8FAFC] p-6">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate("/")}
          className="mb-4"
          data-testid="back-btn"
        >
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <Card className="bg-white shadow-sm">
          <CardContent className="p-8 text-center">
            <div className="w-20 h-20 bg-[#64748B]/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <Wrench className="w-10 h-10 text-[#64748B]" />
            </div>
            <Badge className="mb-4 bg-gray-100 text-gray-600">Unassigned</Badge>
            <h2 className="text-2xl font-bold text-[#1F2933] mb-2 font-mono" style={{ fontFamily: 'Manrope' }}>
              {qrData.code}
            </h2>
            <p className="text-[#6B7280] mb-6">
              This QR code hasn't been assigned to a machine yet.
            </p>
            <Button 
              onClick={handleAssign} 
              className="w-full h-12 bg-[#2563EB] hover:bg-[#1D4ED8]"
              data-testid="assign-btn"
            >
              <Wrench className="w-5 h-5 mr-2" />
              Assign to Machine
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Assigned QR - Show machine info
  const data = qrData?.data || {};
  const status = statusConfig[data.current_status] || statusConfig.working;
  const StatusIcon = status.icon;
  const logs = data.maintenance_logs || [];
  const displayLogs = showAllLogs ? logs : logs.slice(0, 3);

  return (
    <div className="min-h-screen bg-[#F8FAFC] pb-6">
      {/* Header */}
      <div className="bg-white px-6 py-6 border-b border-[#E2E8F0] shadow-sm">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate("/")}
          className="mb-4 text-[#475569] hover:bg-[#F1F5F9]"
          data-testid="back-btn"
        >
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div className="flex items-center gap-4">
          <div className="w-16 h-16 bg-[#2563EB]/10 rounded-2xl flex items-center justify-center">
            <Wrench className="w-8 h-8 text-[#2563EB]" />
          </div>
          <div className="flex-1">
            <h1 className="text-xl font-bold text-[#0F172A]" style={{ fontFamily: 'Manrope' }}>
              {data.machine_name || "Machine"}
            </h1>
            {data.machine_id && (
              <p className="text-[#475569] text-sm font-mono">ID: {data.machine_id}</p>
            )}
            <p className="text-[#94A3B8] text-xs font-mono mt-1">{qrData.code}</p>
          </div>
        </div>
      </div>

      {/* Status Banner */}
      <div className={`mx-6 -mt-3 p-4 rounded-xl ${status.bgColor} border ${status.color.replace('bg-', 'border-')}/30`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <StatusIcon className={`w-6 h-6 ${status.textColor}`} />
            <div>
              <p className={`font-semibold ${status.textColor}`}>{status.label}</p>
              {data.last_service_date && (
                <p className="text-xs text-[#6B7280]">Last Service: {data.last_service_date}</p>
              )}
            </div>
          </div>
          {data.next_service_due && (
            <div className="text-right">
              <p className="text-xs text-[#6B7280]">Next Service</p>
              <p className="text-sm font-medium text-[#1F2933]">{data.next_service_due}</p>
            </div>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="p-6 space-y-4">
        {/* Machine Details */}
        <Card className="bg-white shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <QrCode className="w-4 h-4 text-[#2563EB]" />
              Machine Details
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {data.machine_type && (
              <InfoRow icon={Wrench} label="Type" value={data.machine_type} />
            )}
            {data.location && (
              <InfoRow icon={MapPin} label="Location" value={data.location} />
            )}
            {data.installation_date && (
              <InfoRow icon={Calendar} label="Installed" value={data.installation_date} />
            )}
          </CardContent>
        </Card>

        {/* Contact Card */}
        {(data.operator_name || data.technician_name || data.contact_number) && (
          <Card className="bg-white shadow-sm">
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <User className="w-4 h-4 text-[#2563EB]" />
                Responsibility
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {data.operator_name && (
                <InfoRow icon={User} label="Operator" value={data.operator_name} />
              )}
              {data.technician_name && (
                <InfoRow icon={User} label="Technician" value={data.technician_name} />
              )}
              {data.contact_number && (
                <div className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center">
                      <Phone className="w-4 h-4 text-[#6B7280]" />
                    </div>
                    <div>
                      <p className="text-xs text-[#9CA3AF]">Contact</p>
                      <p className="text-sm font-mono text-[#1F2933]">{data.contact_number}</p>
                    </div>
                  </div>
                  <Button
                    size="sm"
                    onClick={() => handleCall(data.contact_number)}
                    className="bg-[#2563EB] hover:bg-[#1D4ED8]"
                    data-testid="call-btn"
                  >
                    <Phone className="w-4 h-4 mr-1" />
                    Call
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Operating Instructions */}
        {data.operating_instructions && (
          <Card className="bg-white shadow-sm border-l-4 border-l-[#16A34A]">
            <CardHeader 
              className="pb-2 cursor-pointer"
              onClick={() => toggleSection('instructions')}
            >
              <CardTitle className="text-base flex items-center justify-between">
                <div className="flex items-center gap-2 text-[#16A34A]">
                  <BookOpen className="w-4 h-4" />
                  Operating Instructions
                </div>
                {expandedSections.instructions ? (
                  <ChevronUp className="w-5 h-5 text-[#6B7280]" />
                ) : (
                  <ChevronDown className="w-5 h-5 text-[#6B7280]" />
                )}
              </CardTitle>
            </CardHeader>
            {expandedSections.instructions && (
              <CardContent>
                <p className="text-sm text-[#4B5563] whitespace-pre-wrap leading-relaxed">
                  {data.operating_instructions}
                </p>
              </CardContent>
            )}
          </Card>
        )}

        {/* Safety Precautions */}
        {data.safety_precautions && (
          <Card className="bg-white shadow-sm border-l-4 border-l-[#DC2626]">
            <CardHeader 
              className="pb-2 cursor-pointer"
              onClick={() => toggleSection('precautions')}
            >
              <CardTitle className="text-base flex items-center justify-between">
                <div className="flex items-center gap-2 text-[#DC2626]">
                  <AlertTriangle className="w-4 h-4" />
                  Safety Precautions
                </div>
                {expandedSections.precautions ? (
                  <ChevronUp className="w-5 h-5 text-[#6B7280]" />
                ) : (
                  <ChevronDown className="w-5 h-5 text-[#6B7280]" />
                )}
              </CardTitle>
            </CardHeader>
            {expandedSections.precautions && (
              <CardContent>
                <p className="text-sm text-[#4B5563] whitespace-pre-wrap leading-relaxed">
                  {data.safety_precautions}
                </p>
              </CardContent>
            )}
          </Card>
        )}

        {/* Maintenance Logs Timeline */}
        <Card className="bg-white shadow-sm">
          <CardHeader 
            className="pb-2 cursor-pointer"
            onClick={() => toggleSection('logs')}
          >
            <CardTitle className="text-base flex items-center justify-between">
              <div className="flex items-center gap-2">
                <FileText className="w-4 h-4 text-[#2563EB]" />
                Maintenance History
                {logs.length > 0 && (
                  <Badge variant="secondary" className="ml-2">{logs.length}</Badge>
                )}
              </div>
              {expandedSections.logs ? (
                <ChevronUp className="w-5 h-5 text-[#6B7280]" />
              ) : (
                <ChevronDown className="w-5 h-5 text-[#6B7280]" />
              )}
            </CardTitle>
          </CardHeader>
          {expandedSections.logs && (
            <CardContent>
              {logs.length === 0 ? (
                <p className="text-center text-[#9CA3AF] py-6">
                  No maintenance logs recorded yet.
                </p>
              ) : (
                <div className="space-y-4">
                  {displayLogs.map((log, index) => (
                    <MaintenanceLogCard key={index} log={log} isFirst={index === 0} />
                  ))}
                  
                  {logs.length > 3 && (
                    <Button
                      variant="ghost"
                      className="w-full text-[#2563EB]"
                      onClick={() => setShowAllLogs(!showAllLogs)}
                    >
                      {showAllLogs ? (
                        <>Show Less</>
                      ) : (
                        <>Show All {logs.length} Logs</>
                      )}
                    </Button>
                  )}
                </div>
              )}
            </CardContent>
          )}
        </Card>

        {/* QR Info Footer */}
        <div className="flex items-center justify-center gap-2 text-xs text-[#9CA3AF] pt-4">
          <Shield className="w-4 h-4" />
          <span>Machine Maintenance QR System</span>
        </div>
      </div>
    </div>
  );
};

// Maintenance Log Card Component
const MaintenanceLogCard = ({ log, isFirst }) => (
  <div className={`relative pl-6 pb-4 border-l-2 ${isFirst ? 'border-[#2563EB]' : 'border-gray-200'}`}>
    {/* Timeline dot */}
    <div className={`absolute -left-[9px] top-0 w-4 h-4 rounded-full border-2 ${
      isFirst ? 'bg-[#2563EB] border-[#2563EB]' : 'bg-white border-gray-300'
    }`} />
    
    <div className={`p-4 rounded-lg ${isFirst ? 'bg-[#2563EB]/5 border border-[#2563EB]/20' : 'bg-gray-50'}`}>
      {/* Date & Shift */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <span className={`text-sm font-semibold ${isFirst ? 'text-[#2563EB]' : 'text-[#1F2933]'}`}>
            {log.date || "Date not recorded"}
          </span>
          {log.shift && (
            <span className="text-xs px-2 py-0.5 bg-[#2563EB]/10 text-[#2563EB] rounded font-medium">
              {log.shift.replace("_", " ").toUpperCase()}
            </span>
          )}
        </div>
        {isFirst && (
          <Badge className="bg-[#2563EB] text-white text-xs">Latest</Badge>
        )}
      </div>
      
      {/* Breakdown Time & Duration */}
      {(log.breakdown_time || log.maintenance_duration) && (
        <div className="flex flex-wrap gap-4 mb-3 p-2 bg-[#FEF3C7] rounded-md">
          {log.breakdown_time && (
            <div className="flex items-center gap-1">
              <Clock className="w-3 h-3 text-[#D97706]" />
              <span className="text-xs text-[#92400E]">
                <strong>Breakdown:</strong> {log.breakdown_time}
              </span>
            </div>
          )}
          {log.maintenance_duration && (
            <div className="flex items-center gap-1">
              <Clock className="w-3 h-3 text-[#D97706]" />
              <span className="text-xs text-[#92400E]">
                <strong>Duration:</strong> {log.maintenance_duration}
              </span>
            </div>
          )}
        </div>
      )}
      
      {/* Issue */}
      {log.issue && (
        <div className="mb-2">
          <p className="text-xs text-[#9CA3AF] mb-1">Issue Observed</p>
          <p className="text-sm text-[#1F2933]">{log.issue}</p>
        </div>
      )}
      
      {/* Action */}
      {log.action && (
        <div className="mb-2">
          <p className="text-xs text-[#9CA3AF] mb-1">Action Taken</p>
          <p className="text-sm text-[#16A34A]">{log.action}</p>
        </div>
      )}
      
      {/* Parts Replaced */}
      {log.parts_replaced && (
        <div className="mb-2">
          <p className="text-xs text-[#9CA3AF] mb-1">Parts Replaced</p>
          <p className="text-sm text-[#1F2933]">{log.parts_replaced}</p>
        </div>
      )}
      
      {/* Footer: Technician & Next Service */}
      <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-200">
        {log.technician && (
          <div className="flex items-center gap-2">
            <User className="w-4 h-4 text-[#9CA3AF]" />
            <span className="text-xs text-[#6B7280]">{log.technician}</span>
          </div>
        )}
        {log.next_service && (
          <div className="flex items-center gap-2">
            <Calendar className="w-4 h-4 text-[#9CA3AF]" />
            <span className="text-xs text-[#6B7280]">Next: {log.next_service}</span>
          </div>
        )}
      </div>
    </div>
  </div>
);

const InfoRow = ({ icon: Icon, label, value }) => (
  <div className="flex items-start gap-3 py-2 border-b border-gray-100 last:border-0">
    <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 bg-gray-100">
      <Icon className="w-4 h-4 text-[#6B7280]" />
    </div>
    <div className="flex-1 min-w-0">
      <p className="text-xs text-[#9CA3AF]">{label}</p>
      <p className="text-sm text-[#1F2933]">{value}</p>
    </div>
  </div>
);

export default QRPublicPage;
